import {activeThreadIdAtom, messageInputAtom} from '@/components/ChatSection/states'
import formatChatHistory from '@/lib/assistantMessage/formatChatHistory'
import getMetadataFromMessage from '@/lib/assistantMessage/getMetadataFromMessage'
import type {Message, MinimalMessage} from '@/models/messageModel'
import type {Thread} from '@/models/threadModel'
import queries from '@/queries/queries'
import {feedbackMessage} from '@/services/messageService'
import shouldLogError from '@/utils/api/shouldLogError'
import isFunction from '@/utils/types/guards/isFunction'

import useStopGenerating from './useStopGenerating'
interface UseMessagesOptions {
  onStartGenerating?: MemoizedCallback<() => void>
  onStopGenerating?: MemoizedCallback<(isActive: boolean) => void>
  onStartLoadingMessages?: MemoizedCallback<() => void>
  onStopLoadingMessages?: MemoizedCallback<() => void>
  mini?: true
}

export interface UseMessagesReturn {
  submitMessage: MemoizedCallback<(e: React.FormEvent | string, isRegenerate?: boolean) => void>
  isGenerating: boolean
  stopGenerate: MemoizedCallback<
    (except?: symbol | SyntheticEvent, isActive?: boolean, reason?: string) => void
  >
  setActiveThreadId: MemoizedCallbackOrDispatch<string | undefined>
  activeThreadId: string | undefined
  getActiveThreadId: MemoizedCallback<() => string | undefined>
  setVariationThreadId: MemoizedCallbackOrDispatch<string | undefined>
}

export default function useMessages({
  onStartGenerating,
  onStopGenerating,
  onStartLoadingMessages,
  onStopLoadingMessages,
  mini,
}: UseMessagesOptions): UseMessagesReturn {
  const queryClient = useQueryClient()
  const store = useStore()

  const {isGenerating, getIsGenerating, shouldStopThisGenerate, stopGenerate, startGenerate} =
    useStopGenerating({
      onStartGenerating,
      onStopGenerating,
    })

  const [activeThreadId, setActiveThreadIdBase] = useAtom(activeThreadIdAtom)
  const [variationThreadId, setVariationThreadId] = useState<string | undefined>()
  const latestVariationThreadId = useLatest(variationThreadId)

  const setActiveThreadId = useCallback<Dispatch<SetStateAction<string | undefined>>>(
    activeThreadId => {
      setActiveThreadIdBase(prevActiveThreadId => {
        const nextActiveThreadId = isFunction(activeThreadId)
          ? activeThreadId(prevActiveThreadId)
          : activeThreadId
        if (nextActiveThreadId) {
          console.log('resetVariationThreadIdWheneverActiveThreadIdChange', activeThreadId)
          setVariationThreadId(undefined)
        }
        return nextActiveThreadId
      })
    },
    [setActiveThreadIdBase, setVariationThreadId],
  )

  const getActiveThreadId = useCallback(() => {
    return store.get(activeThreadIdAtom)
  }, [store])

  // This messages ref is using for the regenerate feature which involve delete and resend user last message
  const messagesRef = useRef<(Message | MinimalMessage)[]>([])

  // Subscribe immdiately to `messages`
  useEffect(() => {
    const observer = new QueryObserver(
      queryClient,
      queries.getObserveQueryOptions({
        name: 'messages',
        params: [activeThreadId],
      }),
    )

    const currentResult = observer.getCurrentResult()
    messagesRef.current = currentResult.data ?? []

    const unsubscribe = observer.subscribe(result => {
      messagesRef.current = result.data ?? []
      if (!getIsGenerating() && result.isSuccess) {
        onStopLoadingMessages?.()
      }
    })

    return () => {
      unsubscribe()
    }
  }, [activeThreadId, getIsGenerating, onStartLoadingMessages, onStopLoadingMessages, queryClient])

  // Update latest message if author match, otherwise append message
  const updateOrAppendLatestResponseMessage = useCallback(
    (
      threadId: Thread['id'] | undefined,
      message: Message | MinimalMessage,
      forceAppend?: boolean,
    ) => {
      queryClient.setQueryData(
        ...queries.getSetQueryParams('messages', [threadId], messages => {
          if (!messages) {
            return [message]
          }

          const lastMessage = messages[messages.length - 1]

          if (!forceAppend && lastMessage && lastMessage.author === message.author) {
            messages.pop()
          }

          return messages.concat(message)
        }),
      )
    },
    [queryClient],
  )

  const updateLatestMessageMetadata = useCallback(
    (threadId: Thread['id'] | undefined, author: Message['author'], id: string) => {
      queryClient.setQueryData(
        ...queries.getSetQueryParams('messages', [threadId], messages => {
          if (!messages) {
            return []
          }

          const latestMessageIndex = messages.findLastIndex(
            message => message.author === author && !('id' in message),
          )

          if (latestMessageIndex !== -1) {
            const newMessage = {
              ...messages.at(latestMessageIndex),
              id,
            } as Message

            return [
              ...messages.slice(0, latestMessageIndex),
              newMessage,
              ...messages.slice(latestMessageIndex + 1),
            ]
          }
          return messages
        }),
      )
    },
    [queryClient],
  )

  const handleError = useCallback(
    (e: Error, threadId: Thread['id'] | undefined, data?: unknown) => {
      if (shouldLogError(e)) {
        console.error('Error: ', e)
        if (data) console.debug('Info: ', data)
      }

      updateOrAppendLatestResponseMessage(threadId, {
        author: 'assistant',
        content: 'I am sorry but the system is overloaded right now. Please try again later.',
      })

      stopGenerate(undefined, false, 'error occur')
    },
    [stopGenerate, updateOrAppendLatestResponseMessage],
  )

  const submitMessage = useCallback(
    (e: React.FormEvent | string, isRegenerate?: boolean) => {
      const currentThreadId = getActiveThreadId()

      if (getIsGenerating()) return

      let userMessage = store.get(messageInputAtom)

      if (!isRegenerate) {
        if (e && typeof e !== 'string') {
          e.preventDefault()
        } else {
          userMessage = e
        }

        if (!userMessage) return
      } else {
        const lastUserMessage = messagesRef.current.findLast(message => message.author === 'user')
        if (!lastUserMessage) {
          console.error('No user message to regenerate')
          return
        }
        userMessage = lastUserMessage.content
      }

      const messageKey = Symbol(userMessage)

      // Actively stop any running generation
      stopGenerate(messageKey, true, 'new message')

      // TODO: convert to mutation
      if (isRegenerate) {
        // Delete 2 last message
        const deletedMessages = messagesRef.current.slice(-2)

        queryClient.setQueryData(
          ...queries.getSetQueryParams('messages', [currentThreadId], messages => {
            return messages?.slice(0, -2) ?? []
          }),
        )

        deletedMessages.forEach(message => {
          if (!('id' in message) || !message.id) {
            console.error('Id not found for message', message)
            return
          }
          void feedbackMessage({
            messageId: message.id,
            action: 'delete',
          })
        })
      }

      const chatHistory = !mini ? formatChatHistory(messagesRef.current) : undefined

      startGenerate()

      // Display user's message
      updateOrAppendLatestResponseMessage(
        currentThreadId,
        {
          author: 'user',
          content: userMessage,
        },
        true,
      )

      // Display loading message
      updateOrAppendLatestResponseMessage(
        currentThreadId,
        {
          author: 'assistant',
          content: '',
        },
        true,
      )

      const controller = new AbortController()

      // TODO: move to services
      const endpoint = !mini ? '/api/agent/' : '/api/chat/chat/'

      // Send the user's message to the backend and get the bot's response
      // TODO: move to services
      fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          message: userMessage,
          chat_history: chatHistory,
          thread_id: currentThreadId,
          variation_thread_id: latestVariationThreadId.current,
        }),
        signal: controller.signal,
      })
        .then(response => {
          if (!response.body) return

          const body = response.body
          const reader = body.getReader()

          function stop(reason?: string) {
            console.debug('Generation stopped', reason)

            try {
              controller.abort()
            } catch {
              /* empty */
            }

            body.cancel().catch(() => {
              /* empty */
            })
            reader.cancel().catch(() => {
              /* empty */
            })

            // Set metadata
            const {metadata} = getMetadataFromMessage(message)
            if (metadata) {
              // Update latest user message with id
              updateLatestMessageMetadata(currentThreadId, 'user', metadata.user_message_id)
              updateLatestMessageMetadata(
                currentThreadId,
                'assistant',
                metadata.asistant_message_id,
              )

              setActiveThreadId(curr => {
                // Only set metadata if there is no metadata before
                if (curr) return curr
                return metadata.thread_id
              })
            }
          }

          if (shouldStopThisGenerate(messageKey)) {
            stop()
            return
          }

          if (!response.ok) {
            handleError(new Error('Unexpected status code'), currentThreadId, response)
            return undefined
          }

          let message = ''

          void queryClient.invalidateQueries({
            queryKey: queries.getQueryKey('threads'),
            exact: true,
          })

          reader
            .read()
            .then(function pump({
              done,
              value,
            }: ReadableStreamReadResult<Uint8Array>): void | Promise<void> {
              if (shouldStopThisGenerate(messageKey)) {
                stop()
                return
              }

              // Do something with last chunk of data then exit reader`
              if (done) {
                console.debug('DONE:')
                console.debug([message])

                stopGenerate(undefined, false, 'finished')
                stop('finished')
                return
              }
              // Otherwise do something here to process current chunk
              const strValue = new TextDecoder().decode(value as AllowSharedBufferSource)
              message = message.concat(strValue)

              updateOrAppendLatestResponseMessage(currentThreadId, {
                author: 'assistant',
                content: message,
              })
              // Read some more, and call this function again
              return reader.read().then(pump)
            })
            .catch(error => {
              throw error
            })
        })
        .catch((err: Error) => {
          handleError(err, currentThreadId)
        })
    },
    [
      getActiveThreadId,
      getIsGenerating,
      store,
      stopGenerate,
      mini,
      startGenerate,
      updateOrAppendLatestResponseMessage,
      queryClient,
      shouldStopThisGenerate,
      updateLatestMessageMetadata,
      setActiveThreadId,
      handleError,
    ],
  )

  // Stop generate when leave page
  useEffect(() => {
    return () => {
      stopGenerate(Symbol('leave page'), true, 'leave page')
    }
  }, [stopGenerate])

  return {
    submitMessage,
    isGenerating,
    stopGenerate,
    setActiveThreadId,
    activeThreadId,
    getActiveThreadId,
    setVariationThreadId,
  }
}
